import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import {
  UserOrGuest,
  EnterpriseSearchService,
  PassengerType,
  UserService,
  User,
  Guest,
  ModalOpenerService,
  WithSubscriptionComponent
} from '@sabstravtech/obtservices/angular';
import { Traveller, FavouriteGuest } from '@sabstravtech/obtservices/base';
import moment from 'moment';
import { LightningModalTypes } from '../../../../core/classes/modal-types.enum';

@Component({
  selector: 'app-traveller-detail',
  templateUrl: './traveller-detail.component.html',
  styleUrls: ['./traveller-detail.component.scss'],
  standalone: false
})
export class TravellerDetailComponent extends WithSubscriptionComponent implements OnInit {
  faTrash = faTrash;
  faUser = faUser;
  @Input() traveller: UserOrGuest;
  ageType: string;
  isLoadingFrequent: boolean;
  showInvokedLabel = false;
  constructor(
    public searchService: EnterpriseSearchService,
    public userService: UserService,
    private modalOpenerService: ModalOpenerService
  ) {
    super();
  }

  ngOnInit(): void {
    const guestInfo = this.traveller as FavouriteGuest;
    if (!guestInfo?.guestType) {
      const travellerAge = this.traveller.dob
        ? moment().diff(this.traveller.dob, 'years', true)
        : null;
      const ageType = travellerAge ? this.getAgeType(travellerAge) : PassengerType.Adult;
      this.ageType = ageType;
    } else {
      this.ageType = guestInfo.guestType;
    }
    this.showInvokedLabel =
      !!this.traveller.shouldInvokeWhenSelected &&
      this.userService.fullUserDetails.allDetails.value?.canInvokeUser;
  }

  getAgeType(age) {
    switch (true) {
      case age >= 12:
        return PassengerType.Adult;
      case age >= 1 && age <= 11:
        return PassengerType.Child;
      default:
        return PassengerType.Infant;
    }
  }

  addToTraveller(traveller: UserOrGuest) {
    const newTraveller = this.createTravellerFormat(traveller);
    const alreadyInvoked = this.userService.newInvokedUser.value;
    const hasExistingTravellers = this.searchService.getCurrentNoOfTravellers() > 0;

    if (alreadyInvoked && this.userService.bobo?.id) {
      const bobo = this.userService.bobo;
      const isBoboUser = (bobo as any).__typename === 'User';

      const checkCompanyAndProceed = (invokedCompanyId: string) => {
        if (!invokedCompanyId) {
          this.proceedWithAddingTraveller(
            newTraveller,
            traveller,
            alreadyInvoked,
            hasExistingTravellers
          );
          return;
        }

        const guestInfo = traveller as FavouriteGuest;
        const isGuest = !!guestInfo.guestType;
        const travellerCompanyId = isGuest
          ? (traveller as Guest).companyId
          : (traveller as User).company?.id;

        if (travellerCompanyId && travellerCompanyId !== invokedCompanyId) {
          this.modalOpenerService.open(
            LightningModalTypes.ModalCustomWarningComponent,
            { centered: true },
            {
              warningMessage:
                'Travellers from different companies can not be added using favourites.'
            }
          );
          return;
        }

        this.proceedWithAddingTraveller(
          newTraveller,
          traveller,
          alreadyInvoked,
          hasExistingTravellers
        );
      };

      if (isBoboUser) {
        this.subscribe(this.userService.getUser(bobo.id), (invokedUser: User) => {
          checkCompanyAndProceed(invokedUser?.company?.id);
        });
      } else {
        // Guest - get companyId directly from the guest object
        checkCompanyAndProceed((bobo as any).companyId);
      }
      return;
    }

    this.proceedWithAddingTraveller(newTraveller, traveller, alreadyInvoked, hasExistingTravellers);
  }

  private proceedWithAddingTraveller(
    newTraveller: Traveller,
    traveller: UserOrGuest,
    alreadyInvoked: Traveller,
    hasExistingTravellers: boolean
  ) {
    if (
      newTraveller.shouldInvokeWhenSelected &&
      this.userService.fullUserDetails.allDetails.value?.canInvokeUser &&
      !alreadyInvoked &&
      !hasExistingTravellers
    ) {
      const guestInfo = traveller as FavouriteGuest;
      const isGuest = !!guestInfo.guestType;
      const invokedUserOrGuest = {
        id: traveller.id,
        username: isGuest ? traveller.email : (traveller as User).username || traveller.email,
        email: traveller.email,
        forename: traveller.forename,
        surname: traveller.surname,
        phoneNumbers: [],
        title: traveller.title,
        guest: isGuest
      };
      this.userService.setBobo(invokedUserOrGuest as unknown as Traveller);
    }
    this.searchService.addTraveller(newTraveller);
  }

  removeFrequentTravellers(traveller: UserOrGuest) {
    this.isLoadingFrequent = true;
    this.subscribe(this.userService.removeFavouriteTravellers(traveller), (result) => {
      if (result) {
        this.userService.favouriteTravellers.next(
          this.userService.favouriteTravellers.value.filter(
            (frequentTraveller) => frequentTraveller.id !== traveller.id
          )
        );
        this.isLoadingFrequent = false;
      }
    });
  }

  createTravellerFormat(traveller: UserOrGuest): Traveller {
    const guestInfo = traveller as FavouriteGuest;
    const dob = guestInfo.guestType ? guestInfo.guestDob : this.traveller.dob;
    return {
      isChild: this.ageType === PassengerType.Child,
      isInfant: this.ageType === PassengerType.Infant,
      childDOB: null,
      id: traveller.id,
      forename: traveller.forename,
      guest: !!guestInfo.guestType,
      surname: traveller.surname,
      title: traveller.title,
      email: traveller.email,
      name: `${traveller.forename} ${traveller.surname}`,
      dob: dob,
      shouldInvokeWhenSelected: traveller.shouldInvokeWhenSelected
    };
  }
}
